import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { __ } from '../../functions/commom';
import { ReactComponent as DownIcon } from '../../assets/icons/down.svg';

interface SelectorBoxProps {
  label: string;
  value?: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  options: { value: string; label: string }[];
  required?: boolean;
  errorMessage?: string;
  style?: React.CSSProperties;
  hideLabel?: boolean;
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: 'var(--dai-font-size)',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  selectWrapper: {
    position: 'relative',
  },
  select: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '5px',
    transition: 'border-color 0.2s ease',
    appearance: 'none',
    paddingRight: '30px',
    fontSize: 'var(--dai-font-size)',
    '&:hover': {
      border: '1px solid #666666',
    },
    '&:focus': {
      border: '1px solid var(--dai-primary-color)',
      outline: '1px solid var(--dai-primary-color)',
    },
    '&:disabled': {
      backgroundColor: '#f5f5f5',
      cursor: 'not-allowed',
    },
    '&.error': {
      borderColor: '#e53935',
    },
    '& option[disabled]': {
      color: '#aaa',
      cursor: 'not-allowed',
    },
  },
  selectIcon: {
    position: 'absolute',
    right: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    '& svg': {
      fill: 'var(--dai-primary-color)',
      width: '14px',
      height: '14px',
    },
  },
  errorText: {
    color: '#e53935',
    fontSize: '12px',
    marginTop: '5px',
  },
  requiredMark: {
    color: '#e53935',
    marginLeft: '3px',
  },
});

const SelectorBox: React.FC<SelectorBoxProps> = ({
  label,
  value,
  onChange,
  placeholder = __('Select'),
  disabled,
  options,
  required = false,
  errorMessage,
  style,
  hideLabel = false,
  ...rest
}) => {
  const classes = useStyles();
  const [showError, setShowError] = useState(!!errorMessage);

  useEffect(() => {
    setShowError(!!errorMessage);
  }, [errorMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
    setShowError(false);
  };

  const handleBlur = () => {
    setShowError(false);
  };

  return (
    <div className={classes.container}>
      {!hideLabel && (
        <label className={classes.label}>
          {label}
          {required && <span className={classes.requiredMark}>*</span>}
        </label>
      )}
      <div className={classes.selectWrapper}>
        <select
          className={`${classes.select} ${showError ? 'error' : ''}`}
          value={value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          style={style}
          {...rest}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {__(option.label)}
            </option>
          ))}
        </select>
        <div className={classes.selectIcon}>
          <DownIcon />
        </div>
      </div>
      {showError && <div className={classes.errorText}>{errorMessage}</div>}
    </div>
  );
};

export default SelectorBox;
